import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react'
import Grid from '@mui/material/Grid'
import SelectField from '@/v2/components/common/SelectField'
import TextField from '@/v2/components/common/TextField'
import DateOfBirthInput from '@/v2/components/common/DateOfBirthInput'
import Typography from '@/v2/components/common/Typography'
import { RELATIONSHIP_OPTIONS } from '@/v2/constants/insurance'
import { useInsuranceSubscribers } from '@/v2/hooks/insurance/useInsurances'
import getRandomId from '@/v2/utils/random-id'
import { toApiDateString } from '@/v2/utils/dateHelpers'
import {
  genderOptions,
  sequenceCodeOptions,
  normalizeGender,
} from '../../utils'
import Divider from '@/v2/components/common/Divider'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@/v2/components/common/Button'
import { CancelOutlined, EditOutlined, SaveOutlined } from '@mui/icons-material'
import { useUpdateClaimInfo } from '@/v2/hooks/insurance/useClaims'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { usePatientProfile } from '@/v2/hooks/patient/usePatient'

const formatRelationshipOptions = (array = []) =>
  array.map(item => ({
    value: item,
    label: item.charAt(0).toUpperCase() + item.slice(1),
  }))

const formatSubscribersOptions = (subscribers = []) =>
  subscribers.map(item => ({
    value: getRandomId(),
    label: item?.firstName,
    ...item,
  }))

const EMPTY_SUBSCRIBER = {
  subscriber: '',
  firstName: '',
  lastName: '',
  gender: '',
  dob: '',
  memberId: '',
  groupNumber: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipcode: '',
  sequenceCode: '',
  relationship: '',
}

const getValidString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value
  }
  return ''
}

const buildSubscriberInfo = ({
  subscriberValue,
  eligibility,
  foundSubscriber,
  existing,
  patientGender,
  patientDob,
  patientAddress,
}) => {
  const eligSub = eligibility?.response?.subscriber
  const eligPat = eligibility?.response?.patient

  return {
    subscriber: subscriberValue,
    firstName:
      existing?.firstName ||
      eligSub?.firstName ||
      eligPat?.firstName ||
      foundSubscriber?.firstName ||
      '',
    lastName:
      existing?.lastName ||
      eligSub?.lastName ||
      eligPat?.lastName ||
      foundSubscriber?.lastName ||
      '',
    gender: getValidString(
      ...[
        existing?.gender,
        eligSub?.gender,
        eligSub?.sex,
        eligPat?.gender,
        eligPat?.sex,
        foundSubscriber?.gender,
        patientGender,
      ].map(normalizeGender)
    ),
    dob: getValidString(
      existing?.dob,
      eligSub?.dateOfBirth,
      eligSub?.dob,
      eligPat?.dateOfBirth,
      eligPat?.dob,
      foundSubscriber?.dob,
      patientDob
    ),
    memberId:
      existing?.memberId ||
      eligSub?.memberId ||
      eligPat?.memberId ||
      eligibility?.response?.healthplan?.plan?.subscriberId ||
      foundSubscriber?.memberId ||
      foundSubscriber?.plan?.subscriberId ||
      '',
    groupNumber:
      existing?.groupNumber ||
      eligSub?.groupNumber ||
      eligSub?.group ||
      eligPat?.groupNumber ||
      eligPat?.group ||
      eligibility?.response?.healthplan?.plan?.groupNumber ||
      foundSubscriber?.groupNumber ||
      foundSubscriber?.plan?.groupNumber ||
      '',
    address1:
      existing?.address1 ||
      eligSub?.address?.address1 ||
      eligPat?.address?.address1 ||
      foundSubscriber?.address?.address1 ||
      foundSubscriber?.address1 ||
      patientAddress?.address1 ||
      '',
    address2:
      existing?.address2 ||
      eligSub?.address?.address2 ||
      eligPat?.address?.address2 ||
      foundSubscriber?.address?.address2 ||
      foundSubscriber?.address2 ||
      patientAddress?.address2 ||
      '',
    city:
      existing?.city ||
      eligSub?.address?.city ||
      eligPat?.address?.city ||
      foundSubscriber?.address?.city ||
      foundSubscriber?.city ||
      patientAddress?.city ||
      '',
    state:
      existing?.state ||
      eligSub?.address?.state ||
      eligPat?.address?.state ||
      foundSubscriber?.address?.state ||
      foundSubscriber?.state ||
      patientAddress?.state ||
      '',
    zipcode:
      existing?.zipcode ||
      eligSub?.address?.zipcode ||
      eligPat?.address?.zipcode ||
      foundSubscriber?.address?.zipcode ||
      foundSubscriber?.zipcode ||
      patientAddress?.zipcode ||
      '',
    relationship: existing?.relationship || foundSubscriber?.relationship || '',
    sequenceCode: existing?.sequenceCode || 'primary',
  }
}

const Subscriber = ({
  insuranceId,
  isEdit,
  values = {},
  error = {},
  editAllowed = false,
  onChange,
  patient,
  claimId,
  patientId,
  eligibility,
  viewOnly = false,
}) => {
  const theme = useTheme()
  const { patient: profilePatient } = usePatientProfile(
    patientId || patient?.id
  )
  const resolveField = (...keys) => {
    for (const src of [patient, profilePatient]) {
      if (!src) continue
      for (const key of keys) {
        const val = key.includes('.')
          ? key.split('.').reduce((o, k) => o?.[k], src)
          : src[key]
        if (val && typeof val === 'string') return val.trim()
      }
    }
    return ''
  }

  const patientGender = patient?.gender || profilePatient?.gender
  const patientDob = patient?.dob || profilePatient?.dob
  const patientAddress1 = resolveField(
    'address1',
    'address_1',
    'address.address1'
  )
  const patientAddress2 = resolveField(
    'address2',
    'address_2',
    'address.address2'
  )
  const patientCity = resolveField('city', 'address.city')
  const patientState = resolveField('state', 'address.state')
  const patientZipcode = resolveField('zipcode', 'address.zipcode')
  const [selectedSubscriber, setSelectedSubscriber] = useState('')
  const hasAutoSelectedRef = useRef(false)
  const autoSelectFiredRef = useRef(false)
  const currentInsuranceRef = useRef(values?.insuranceId || insuranceId)
  const onChangeRef = useRef(onChange)
  const { subscribers } = useInsuranceSubscribers(
    values?.insuranceId || insuranceId
  )
  const { subscriber } = values || {}
  const [allowSubscriberEdit, setAllowSubscriberEdit] = useState(false)
  const [subscriberData, setSubscriberData] = useState(subscriber)
  const { updateClaim } = useUpdateClaimInfo(insuranceId, claimId, patientId)
  const { onOpenSnackbar } = useSnackbarContext()

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const handleSave = async () => {
    const dob = toApiDateString(subscriberData?.dob)
    if (!dob) {
      onOpenSnackbar('Enter a valid date of birth as MM/DD/YYYY', 'error')
      return
    }

    const res = await updateClaim({
      body: {
        insuranceId,
        subscriberAttributes: { ...subscriberData, dob },
      },
    })
    // No else branch: requestMiddleware already surfaces the failure, and
    // res.error is a field-errors object rather than a message.
    if (!res.error) {
      setAllowSubscriberEdit(false)
      onOpenSnackbar('Subscriber information updated successfully', 'success')
    }
  }

  const handleCancel = () => {
    setAllowSubscriberEdit(false)
    setSubscriberData(subscriber)
  }

  const subscribersOptions = useMemo(
    () => formatSubscribersOptions(subscribers),
    [subscribers]
  )

  const patientAddress = useMemo(
    () => ({
      address1: patientAddress1,
      address2: patientAddress2,
      city: patientCity,
      state: patientState,
      zipcode: patientZipcode,
    }),
    [
      patientAddress1,
      patientAddress2,
      patientCity,
      patientState,
      patientZipcode,
    ]
  )

  const handleChangeSubscriber = useCallback(
    value => {
      if (!value) {
        setSelectedSubscriber('')
        setSubscriberData(EMPTY_SUBSCRIBER)
        onChangeRef.current(EMPTY_SUBSCRIBER)
        return
      }

      setSelectedSubscriber(`${value}`)

      const subscriberIndex = subscribersOptions.findIndex(
        item => item.value === value
      )
      const subscriberInfo = buildSubscriberInfo({
        subscriberValue: value,
        eligibility,
        foundSubscriber:
          subscriberIndex >= 0 ? subscribers[subscriberIndex] : null,
        patientGender,
        patientDob,
        patientAddress,
      })

      setSubscriberData(subscriberInfo)
      onChangeRef.current(subscriberInfo)
    },
    [
      subscribersOptions,
      subscribers,
      eligibility,
      patientGender,
      patientDob,
      patientAddress,
    ]
  )

  useEffect(() => {
    const newInsurance = values?.insuranceId || insuranceId
    if (currentInsuranceRef.current !== newInsurance) {
      currentInsuranceRef.current = newInsurance
      hasAutoSelectedRef.current = false
      setSelectedSubscriber('')
      setSubscriberData(EMPTY_SUBSCRIBER)
      onChangeRef.current(EMPTY_SUBSCRIBER)
    }
  }, [values?.insuranceId, insuranceId])

  useEffect(() => {
    if (
      !isEdit &&
      subscribersOptions?.length > 0 &&
      subscribers?.length > 0 &&
      !hasAutoSelectedRef.current &&
      (values?.insuranceId || insuranceId)
    ) {
      const [firstSubscriber] = subscribersOptions
      if (firstSubscriber?.value) {
        const subscriberInfo = buildSubscriberInfo({
          subscriberValue: firstSubscriber.value,
          eligibility,
          foundSubscriber: subscribers[0],
          patientGender,
          patientDob,
          patientAddress,
        })

        setSelectedSubscriber(firstSubscriber.value)
        setSubscriberData(subscriberInfo)
        onChangeRef.current(subscriberInfo)
        hasAutoSelectedRef.current = true
        autoSelectFiredRef.current = true
      }
    }
  }, [
    subscribersOptions,
    isEdit,
    subscribers,
    values?.insuranceId,
    insuranceId,
    eligibility,
    patientGender,
    patientDob,
    patientAddress,
  ])

  useEffect(() => {
    if (
      isEdit &&
      subscribersOptions?.length > 0 &&
      !hasAutoSelectedRef.current
    ) {
      let matchingSubscriber = null
      if (subscriber) {
        matchingSubscriber = subscribersOptions.find(option => {
          const sub = subscribers[subscribersOptions.indexOf(option)]
          return (
            sub?.firstName === subscriber?.firstName &&
            sub?.lastName === subscriber?.lastName &&
            sub?.dob === subscriber?.dob
          )
        })
      }

      const subscriberToSelect = matchingSubscriber || subscribersOptions[0]

      if (subscriberToSelect?.value) {
        const subscriberIndex = subscribersOptions.indexOf(subscriberToSelect)
        const subscriberInfo = buildSubscriberInfo({
          subscriberValue: subscriberToSelect.value,
          eligibility,
          foundSubscriber: subscribers[subscriberIndex],
          existing: subscriber,
          patientGender,
          patientDob,
          patientAddress,
        })

        setSelectedSubscriber(subscriberToSelect.value)
        setSubscriberData(subscriberInfo)
        onChangeRef.current(subscriberInfo)
        hasAutoSelectedRef.current = true
        autoSelectFiredRef.current = true
      }
    }
  }, [
    isEdit,
    subscribersOptions,
    subscriber,
    subscribers,
    eligibility,
    patientGender,
    patientDob,
    patientAddress,
  ])

  const changeRelationship = value => {
    if (value !== 'self') onChange({ relationship: value })
    else
      onChange({
        relationship: 'self',
        firstName: patient?.firstName,
        lastName: patient?.lastName,
      })
  }

  useEffect(() => {
    if (autoSelectFiredRef.current) {
      autoSelectFiredRef.current = false
      return
    }
    if (subscriber) {
      setSubscriberData(subscriber)
    }
  }, [subscriber])

  const handleDateChange = value => {
    onChange({ dob: value })
  }

  useEffect(() => {
    if (editAllowed) {
      setAllowSubscriberEdit(true)
    }
  }, [editAllowed])

  // Update gender from profile when it loads and gender is not set
  useEffect(() => {
    if (profilePatient?.gender) {
      setSubscriberData(prev => {
        if (prev && !prev.gender) {
          const normalizedGender = normalizeGender(profilePatient.gender)
          onChangeRef.current({ gender: normalizedGender })
          return { ...prev, gender: normalizedGender }
        }
        return prev
      })
    }
  }, [profilePatient?.gender])

  // Sync address from profile when it loads and address is not set
  useEffect(() => {
    if (patientAddress1) {
      setSubscriberData(prev => {
        if (prev && !prev.address1) {
          onChangeRef.current(patientAddress)
          return { ...prev, ...patientAddress }
        }
        return prev
      })
    }
  }, [patientAddress1, patientAddress])

  return (
    <>
      <Grid item xs={12} sx={{ marginTop: '12px' }}>
        <Typography variant="h5" bold fontSize="16px">
          Subscriber
        </Typography>
        <Divider margin="5px 0px" />
      </Grid>

      <>
        {(!isEdit || allowSubscriberEdit) && (
          <>
            <Grid item xs={12} sm={8}>
              <SelectField
                fullWidth
                noMargin
                name="subscriber"
                label="Subscriber"
                placeholder="Choose an option"
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                value={selectedSubscriber}
                options={subscribersOptions}
                onChange={handleChangeSubscriber}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
          </>
        )}
        {subscriber && (
          <>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="firstName"
                label="First Name"
                placeholder="First Name"
                value={subscriberData?.firstName || ''}
                error={error['subscriber.firstName']}
                onChange={value => onChange({ firstName: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="lastName"
                label="Last Name"
                placeholder="Last Name"
                value={subscriberData?.lastName || ''}
                error={error['subscriber.lastName']}
                onChange={value => onChange({ lastName: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <DateOfBirthInput
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                label="Date of birth"
                value={subscriberData?.dob || ''}
                error={error['subscriber.dob']}
                onChange={handleDateChange}
                disabled={!allowSubscriberEdit}
                helpText="Enter date as MM/DD/YYYY"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <SelectField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                noMargin
                name="gender"
                label="Gender"
                placeholder="Choose an option"
                options={genderOptions}
                value={subscriberData?.gender || ''}
                error={error['subscriber.gender']}
                onChange={value => onChange({ gender: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <SelectField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                noMargin
                name="relationship"
                label="Relationship"
                placeholder="Choose an option"
                options={formatRelationshipOptions(RELATIONSHIP_OPTIONS)}
                value={subscriberData?.relationship || ''}
                error={error['subscriber.relationship']}
                onChange={value => changeRelationship(value)}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="memberId"
                label="Member ID"
                placeholder="E.g.: 123456789"
                value={subscriberData?.memberId || ''}
                error={error['subscriber.memberId']}
                onChange={value => onChange({ memberId: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="groupNumber"
                label="Group Number"
                placeholder="E.g.: 123456789"
                value={subscriberData?.groupNumber || ''}
                error={error['subscriber.groupNumber']}
                onChange={value => onChange({ groupNumber: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <SelectField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                noMargin
                name="sequenceCode"
                label="Sequence Code"
                placeholder="Choose an option"
                options={sequenceCodeOptions}
                value={subscriberData?.sequenceCode || ''}
                error={error['subscriber.sequenceCode']}
                onChange={value => onChange({ sequenceCode: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="address1"
                label="Address"
                value={subscriberData?.address1 || ''}
                onChange={value => onChange({ address1: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="address2"
                label="Address 2"
                value={subscriberData?.address2 || ''}
                onChange={value => onChange({ address2: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="zipcode"
                label="Postal Code"
                value={subscriberData?.zipcode || ''}
                onChange={value => onChange({ zipcode: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="city"
                label="City"
                value={subscriberData?.city || ''}
                onChange={value => onChange({ city: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowSubscriberEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="state"
                label="State"
                value={subscriberData?.state || ''}
                onChange={value => onChange({ state: value })}
                disabled={!allowSubscriberEdit}
              />
            </Grid>
          </>
        )}
        {isEdit && !viewOnly && (
          <Grid item xs={12}>
            <Box display="flex" flexDirection="row">
              <Button
                variant="text"
                disableElevation
                aria-label={allowSubscriberEdit ? 'Cancel' : 'Edit Subscriber'}
                startIcon={
                  allowSubscriberEdit ? (
                    <CancelOutlined color="error" />
                  ) : (
                    <EditOutlined color="link" />
                  )
                }
                onClick={() => {
                  if (allowSubscriberEdit) {
                    handleCancel()
                  }
                  setAllowSubscriberEdit(!allowSubscriberEdit)
                }}
                sx={{
                  color: '#000',
                  marginBottom: '4px',
                  '&:hover': {
                    background: theme.palette.silver.dark,
                  },
                }}
              >
                {allowSubscriberEdit ? 'Cancel' : 'Edit'}
              </Button>
              {allowSubscriberEdit && (
                <Button
                  variant="text"
                  disableElevation
                  startIcon={<SaveOutlined color="link" />}
                  onClick={handleSave}
                  sx={{
                    color: '#000',
                    marginBottom: '4px',
                    '&:hover': {
                      background: theme.palette.silver.dark,
                    },
                    marginLeft: '10px',
                  }}
                >
                  Save
                </Button>
              )}
            </Box>
          </Grid>
        )}
      </>
    </>
  )
}

export default Subscriber
